import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock3, User } from "lucide-react";
import { fetchPosts } from "@/lib/api";
import SEO from "@/components/SEO";

function formatDate(iso) {
    if (!iso) return "";
    try {
        const d = typeof iso === "string" ? new Date(iso) : iso;
        return d.toLocaleDateString("it-CH", { year: "numeric", month: "long", day: "numeric" });
    } catch {
        return "";
    }
}

export default function Blog() {
    const [state, setState] = useState({ loading: true, posts: [], error: null });

    useEffect(() => {
        fetchPosts()
            .then((posts) => setState({ loading: false, posts, error: null }))
            .catch((e) => setState({ loading: false, posts: [], error: e.message }));
    }, []);

    const [featured, ...rest] = state.posts;

    return (
        <>
            <SEO
                title="Blog · Articoli e approfondimenti su primo soccorso"
                description="Notizie, approfondimenti normativi e casi pratici sulla formazione di primo soccorso, BLS-AED e gestione defibrillatori. Aggiornamenti dal team 7MED."
                path="/blog"
                keywords="blog primo soccorso, articoli BLS-AED, normativa OLL3, manutenzione AED"
                structuredData={{
                    "@context": "https://schema.org",
                    "@type": "Blog",
                    name: "7MED Blog",
                    url: "https://7med.ch/blog",
                    publisher: { "@id": "https://7med.ch/#organization" },
                    inLanguage: "it",
                    blogPost: state.posts.map((p) => ({
                        "@type": "BlogPosting",
                        headline: p.title,
                        url: `https://7med.ch/blog/${p.slug}`,
                        datePublished: p.published_at || p.created_at,
                    })),
                }}
                breadcrumbs={[{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }]}
            />
            <section className="bg-white">
                <div className="container-7m py-16 sm:py-20 lg:py-28">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 items-end">
                        <div className="lg:col-span-8">
                            <div className="label-eyebrow text-brand">Blog</div>
                            <h1 className="mt-4 font-heading font-black text-4xl sm:text-6xl lg:text-8xl tracking-tighter leading-[1.05]">
                                Articoli,<br /><span className="text-brand">approfondimenti,</span> notizie.
                            </h1>
                        </div>
                        <div className="lg:col-span-4 lg:col-start-9">
                            <p className="text-readable-lg">
                                Normative sul primo soccorso, casi pratici, aggiornamenti normativi e consigli per mantenere operativo il tuo defibrillatore.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {state.loading && (
                <div className="container-7m py-20 text-center">
                    <div className="w-2 h-2 bg-brand rounded-full animate-pulse-dot mx-auto"></div>
                    <div className="mt-4 text-xs font-mono uppercase tracking-[0.2em] text-ink-muted">Caricamento articoli…</div>
                </div>
            )}

            {!state.loading && state.posts.length === 0 && (
                <div className="container-7m py-28 text-center border-t border-ink/10" data-testid="blog-empty">
                    <div className="label-eyebrow text-brand">Presto</div>
                    <h2 className="mt-4 font-heading font-black text-4xl tracking-tighter">Primo articolo in arrivo.</h2>
                    <p className="mt-4 text-ink-muted">Stiamo preparando contenuti pensati per te. Torna a trovarci.</p>
                </div>
            )}

            {!state.loading && featured && (
                <section className="py-12 lg:py-20 bg-[#FAFAFA] border-t border-ink/10">
                    <div className="container-7m">
                        <Link to={`/blog/${featured.slug}`} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 group" data-testid="blog-featured">
                            <div className="lg:col-span-7 aspect-[16/10] overflow-hidden bg-ink/5">
                                {featured.cover_image ? (
                                    <img src={featured.cover_image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                ) : (
                                    <div className="w-full h-full bg-ink flex items-center justify-center">
                                        <div className="font-heading font-black text-8xl text-brand tracking-tighter">7MED</div>
                                    </div>
                                )}
                            </div>
                            <div className="lg:col-span-5 flex flex-col justify-center">
                                <div className="label-eyebrow text-brand">{featured.category}</div>
                                <h2 className="mt-4 font-heading font-black text-3xl lg:text-5xl tracking-tighter leading-tight group-hover:text-brand transition-colors">{featured.title}</h2>
                                <p className="mt-4 text-ink-muted leading-relaxed">{featured.summary}</p>
                                <div className="mt-6 flex items-center gap-4 text-xs font-mono uppercase tracking-wider text-ink-muted">
                                    <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{featured.author}</span>
                                    <span className="flex items-center gap-1"><Clock3 className="w-3.5 h-3.5" />{featured.reading_minutes} min</span>
                                    <span>{formatDate(featured.published_at || featured.created_at)}</span>
                                </div>
                                <div className="mt-8 inline-flex items-center gap-2 font-bold uppercase tracking-wider text-sm group-hover:text-brand">
                                    Leggi articolo <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </div>
                            </div>
                        </Link>
                    </div>
                </section>
            )}

            {rest.length > 0 && (
                <section className="py-16 lg:py-24 bg-white">
                    <div className="container-7m">
                        <div className="label-eyebrow text-brand mb-8">Altri articoli</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10">
                            {rest.map((p) => (
                                <Link to={`/blog/${p.slug}`} key={p.slug} className="bg-white p-6 hover:bg-ink hover:text-white transition-colors duration-300 group flex flex-col" data-testid={`blog-card-${p.slug}`}>
                                    {p.cover_image && (
                                        <div className="aspect-[16/10] overflow-hidden -mx-6 -mt-6 mb-5 bg-ink/5">
                                            <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" />
                                        </div>
                                    )}
                                    <div className="label-eyebrow text-brand">{p.category}</div>
                                    <h3 className="mt-3 font-heading font-bold text-2xl leading-tight">{p.title}</h3>
                                    <p className="mt-3 text-sm text-ink-muted group-hover:text-white/70 leading-relaxed line-clamp-3">{p.summary}</p>
                                    <div className="mt-auto pt-5 text-xs font-mono uppercase tracking-wider text-ink-muted group-hover:text-white/50 flex items-center justify-between">
                                        <span>{formatDate(p.published_at || p.created_at)}</span>
                                        <span>{p.reading_minutes} min</span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </>
    );
}
